import {faPlus, faXmark} from '@fortawesome/pro-regular-svg-icons';
import {FontAwesomeIcon} from '@fortawesome/react-fontawesome';
import {Button, Flex, Input, Select, Space, Tooltip, Tour, TourProps} from 'antd';
import {keyBy, merge, values} from 'lodash';
import {memo, useEffect, useMemo, useRef, useState} from 'react';
import {z as r} from 'zod';

import {useBrands} from '../../../../provider/brands/brand-hook';
import {useLocale} from '../../../../provider/locale/locale-hook';
import {Locale} from '../../../../provider/locale/localization';
import {useModal} from '../../../../provider/modal/modal-hook';
import {usePersonality} from '../../../../provider/personality/use-personality';
import {useSnackbar} from '../../../../provider/snackbar/snackbar-hook';
import {useSystem} from '../../../../provider/system/system-hook';
import {
    ManageReviewStatusError,
    ReviewStatusType,
    updateReviewStatus,
    useCreateReviewStatus,
    useDeleteReviewStatus,
    useReviewStatusList,
} from '../../../../service/reviews/reviews-statuses';
import {ReviewDataType} from '../../../../service/reviews/reviews-type';
import {useUserRoles} from '../../../../service/user-management/user-management-user';
import {handleFilterOption} from '../../../../util/antd/select-helper';
import {classNames} from '../../../../util/css';
import {HttpError} from '../../../../util/error';
import {getFromLocalStorage, saveToLocalStorage} from '../../../../util/local-storage';

import {OverflowTitle} from './overflow-title';
import {IS_SHOW_CUSTOM_STATUS_TOUR_LOCALSTORAGE_KEY} from './review-status-const';
import * as styles from './review-status.scss';

const {Option} = Select;

type PropsType = {
    updateReview: (reviewId: number, updateData: Partial<ReviewDataType>) => void;
    reviewId: number;
    processStatus: string;
    className?: string;
    onChange?: () => void;
    isFirstReview?: boolean;
    /** Передаётся с list-страницы: тур показывается только у видимого селекта (в хедере при ширине >500px, в теле при ≤500px) */
    isTourEligible?: boolean;
};

// eslint-disable-next-line max-statements, complexity
export const ReviewStatus = memo(function ReviewStatusInner(props: PropsType): JSX.Element {
    const {updateReview, reviewId, processStatus, className, onChange, isFirstReview, isTourEligible} = props;

    const {screen} = useSystem();
    const isTourEligibleByDevice =
        !screen.isMobile && (typeof isTourEligible === 'boolean' ? isTourEligible : Boolean(isFirstReview));

    const {localeName, getLocalizedString} = useLocale();
    const {snackbar} = useSnackbar();
    const {modal} = useModal();
    const {data: ownUser} = useUserRoles();
    const isOwner = Boolean(ownUser?.role?.isOwner);
    const {data: reviewStatusList, isFetching: isReviewStatusListInProgress} = useReviewStatusList(localeName);
    const {isLoading} = useBrands();
    const {mutateAsync: createStatus, isLoading: isCreatingStatusInProgress} = useCreateReviewStatus(localeName);
    const {mutateAsync: deleteStatus, isLoading: isDeletingStatusInProgress} = useDeleteReviewStatus(localeName);
    const createButtonRef = useRef<HTMLButtonElement | null>(null);

    const [isUpdating, setIsUpdating] = useState<boolean>(false);
    const [innerStatus, setInnerStatus] = useState<string>(processStatus);
    const [isOpen, setIsOpen] = useState(false);
    const [isCreatingStatus, setIsCreatingStatus] = useState<boolean>(false);
    const [newStatusValue, setNewStatusValue] = useState<string>('');
    const [newlyCreatedStatusCode, setNewlyCreatedStatusCode] = useState<string | null>(null);
    const [isTourOpen, setIsTourOpen] = useState<boolean>(false);

    const status = useMemo(() => {
        return reviewStatusList?.statuses.find((statusItem) => statusItem.statusCode === innerStatus) || null;
    }, [innerStatus, reviewStatusList?.statuses]);

    const isStatusMissingFromList = useMemo(() => {
        if (!reviewStatusList?.statuses) {
            return false;
        }

        return !reviewStatusList.statuses.some((statusItem) => statusItem.statusCode === innerStatus);
    }, [innerStatus, reviewStatusList?.statuses]);

    const {reviewStatusesMapping} = usePersonality();

    const mappedStatuses = useMemo(() => {
        if (!reviewStatusesMapping) {
            return null;
        }

        const results = values(
            merge(keyBy(reviewStatusesMapping, 'statusCode'), keyBy(reviewStatusList?.statuses, 'statusCode'))
        ).filter((item) => item.availableStatus);

        return new Map<string, Array<string>>(results.map((item) => [item.statusCode, item.availableStatus]));
    }, [reviewStatusesMapping, reviewStatusList?.statuses]);

    const shouldShowTour = isTourEligibleByDevice && !mappedStatuses && isOwner;

    useEffect(() => {
        setInnerStatus(processStatus);
    }, [processStatus]);

    useEffect(() => {
        let timeoutId: NodeJS.Timeout | null = null;

        if (newlyCreatedStatusCode && isOpen && reviewStatusList?.statuses) {
            timeoutId = setTimeout(() => {
                const dropdownElement = document.querySelector('.ant-select-dropdown:not(.ant-select-dropdown-hidden)');

                if (dropdownElement) {
                    const listElement = dropdownElement.querySelector('.rc-virtual-list-holder');

                    if (listElement) {
                        listElement.scrollTop = listElement.scrollHeight;
                    }
                }

                setNewlyCreatedStatusCode(null);
            }, 100);
        }

        return () => {
            if (timeoutId !== null) {
                clearTimeout(timeoutId);
            }
        };
    }, [newlyCreatedStatusCode, isOpen, reviewStatusList?.statuses]);

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;

        if (
            shouldShowTour &&
            !isReviewStatusListInProgress &&
            !isLoading &&
            getFromLocalStorage(IS_SHOW_CUSTOM_STATUS_TOUR_LOCALSTORAGE_KEY, r.boolean(), true)
        ) {
            timer = setTimeout(() => {
                setIsOpen(true);
            }, 1300);
        }

        return () => {
            if (timer !== null) {
                clearTimeout(timer);
            }
        };
    }, [shouldShowTour, isReviewStatusListInProgress, isLoading]);

    useEffect(() => {
        let timer: NodeJS.Timeout | null = null;

        if (
            isOpen &&
            shouldShowTour &&
            getFromLocalStorage(IS_SHOW_CUSTOM_STATUS_TOUR_LOCALSTORAGE_KEY, r.boolean(), true)
        ) {
            timer = setTimeout(() => {
                setIsTourOpen(true);
            }, 600);
        }

        return () => {
            if (timer !== null) {
                clearTimeout(timer);
            }
        };
    }, [isOpen, shouldShowTour]);

    function handleCloseTour() {
        setIsTourOpen(false);
        saveToLocalStorage(false, IS_SHOW_CUSTOM_STATUS_TOUR_LOCALSTORAGE_KEY);
    }

    const tourSteps: TourProps['steps'] = useMemo(
        () => [
            {
                title: <Locale stringKey="REVIEWS__STATUS__TOUR_TITLE" />,
                description: <Locale stringKey="REVIEWS__STATUS__TOUR_DESCRIPTION" />,
                placement: 'left',
                target: () => createButtonRef.current ?? document.body,
            },
        ],
        []
    );

    async function onStatusSelect(selectedStatus: string) {
        try {
            setIsUpdating(true);
            setInnerStatus(selectedStatus);
            await updateReviewStatus(reviewId, {status: selectedStatus});
            await updateReview(reviewId, {processStatus: selectedStatus});

            if (onChange) {
                onChange();
            }
        } catch (error: unknown) {
            const description =
                error instanceof ManageReviewStatusError ? (
                    <span>{error.jsonData.status}</span>
                ) : (
                    <Locale stringKey="ERROR__SOMETHING_WENT_WRONG_DETAILS" />
                );

            snackbar.error({
                message: <Locale stringKey="ERROR__SOMETHING_WENT_WRONG" />,
                description,
            });

            if (!(error instanceof HttpError)) {
                throw error;
            }
        } finally {
            setIsUpdating(false);
        }
    }

    function handleCreateStatusClick() {
        setIsCreatingStatus(true);
        handleCloseTour();
    }

    function handleCancelCreateStatus() {
        setIsCreatingStatus(false);
        setNewStatusValue('');
    }

    async function handleSaveNewStatus() {
        if (!newStatusValue.trim()) {
            return;
        }

        const createdStatus = await createStatus({title: newStatusValue});

        setIsCreatingStatus(false);
        setNewStatusValue('');

        if (createdStatus) {
            setNewlyCreatedStatusCode(createdStatus.statusCode);
            setIsOpen(true);
        }
    }

    function handleCloseSelect(value: boolean) {
        setIsOpen(value);

        if (!value && isTourOpen) {
            handleCloseTour();
        }
    }

    function handleDeleteStatus(event: React.MouseEvent, statusCode: string) {
        event.stopPropagation();

        const availableStatuses =
            reviewStatusList?.statuses.filter((statusItem) => statusItem.statusCode !== statusCode) || [];
        const defaultReplacementStatus = availableStatuses.find((statusItem) => statusItem.isDefault);
        let selectedReplacementStatus = defaultReplacementStatus?.statusCode || availableStatuses[0]?.statusCode || '';

        modal.confirm({
            cancelButtonProps: {type: 'default'},
            cancelText: <Locale stringKey="BUTTON__CANCEL" />,
            content: (
                <div>
                    <div className={styles.ReviewStatus_modalDescription}>
                        <Locale stringKey="REVIEWS__STATUS__DELETE_MODAL_DESCRIPTION" />
                    </div>
                    <Select
                        className={styles.ReviewStatus_modalSelect}
                        defaultValue={selectedReplacementStatus}
                        onChange={(value) => {
                            selectedReplacementStatus = value;
                        }}
                        placeholder={getLocalizedString('REVIEWS__STATUS__DELETE_MODAL_SELECT_PLACEHOLDER')}
                    >
                        {availableStatuses.map((statusItem) => (
                            <Option key={statusItem.statusCode} value={statusItem.statusCode}>
                                {statusItem.title}
                            </Option>
                        ))}
                    </Select>
                </div>
            ),
            maskClosable: true,
            okButtonProps: {type: 'primary', danger: true},
            okText: <Locale stringKey="REVIEWS__STATUS__DELETE_MODAL_CONFIRM" />,
            title: <Locale stringKey="REVIEWS__STATUS__DELETE_MODAL_TITLE" />,
            onOk: async () => {
                if (selectedReplacementStatus) {
                    await deleteStatus({
                        statusCode,
                        replacementStatusCode: selectedReplacementStatus,
                    });
                }
            },
        });
    }

    function renderStatusOption(reviewStatus: ReviewStatusType) {
        return (
            <div className={styles.ReviewStatus_optionContent}>
                <OverflowTitle className={styles.ReviewStatus_optionTitle} title={reviewStatus.title} />
                {isOwner && !reviewStatus.isDefault && (
                    <Tooltip title={<Locale stringKey="REVIEWS__STATUS__DELETE_STATUS_TOOLTIP" />}>
                        <Button
                            className={styles.ReviewStatus_deleteButton}
                            disabled={isDeletingStatusInProgress}
                            icon={<FontAwesomeIcon icon={faXmark} />}
                            onClick={(event) => handleDeleteStatus(event, reviewStatus.statusCode)}
                            size="small"
                            type="text"
                        />
                    </Tooltip>
                )}
            </div>
        );
    }

    function dropdownRender(menu: React.ReactElement) {
        return (
            <div>
                {menu}
                {isOwner && !mappedStatuses && (
                    <Flex>
                        {!isCreatingStatus ? (
                            <Button
                                className={styles.ReviewStatus_createButton}
                                onClick={handleCreateStatusClick}
                                ref={createButtonRef}
                                type="link"
                            >
                                <FontAwesomeIcon icon={faPlus} />
                                <Locale stringKey="REVIEWS__STATUS__CREATE_STATUS_BUTTON" />
                            </Button>
                        ) : (
                            <Space className={styles.ReviewStatus_createForm} direction="vertical">
                                <Input
                                    autoFocus
                                    className={styles.ReviewStatus_createFormInput}
                                    disabled={isCreatingStatusInProgress}
                                    onChange={(event_) => setNewStatusValue(event_.target.value)}
                                    onPressEnter={handleSaveNewStatus}
                                    placeholder={getLocalizedString('REVIEWS__STATUS__CREATE_STATUS_PLACEHOLDER')}
                                    style={{caretColor: 'auto'}}
                                    value={newStatusValue}
                                />
                                <Space className={styles.ReviewStatus_createFormButtons}>
                                    <Button
                                        disabled={isCreatingStatusInProgress}
                                        onClick={handleCancelCreateStatus}
                                        size="small"
                                    >
                                        <Locale stringKey="BUTTON__CANCEL" />
                                    </Button>
                                    <Button
                                        disabled={!newStatusValue.trim() || isCreatingStatusInProgress}
                                        loading={isCreatingStatusInProgress}
                                        onClick={handleSaveNewStatus}
                                        size="small"
                                        type="primary"
                                    >
                                        <Locale stringKey="BUTTON__SAVE" />
                                    </Button>
                                </Space>
                            </Space>
                        )}
                    </Flex>
                )}
            </div>
        );
    }

    return (
        <>
            <div className={classNames(className, styles.ReviewStatus, isOpen ? styles.ReviewStatus__focused : '')}>
                <Tooltip
                    title={
                        isStatusMissingFromList ? <Locale stringKey="REVIEWS__STATUS__DELETED_STATUS_TOOLTIP" /> : null
                    }
                >
                    <Select<Array<string>>
                        className={styles.ReviewStatus_select}
                        disabled={isStatusMissingFromList}
                        dropdownRender={dropdownRender}
                        dropdownStyle={{minWidth: 320}}
                        filterOption={handleFilterOption}
                        getPopupContainer={(triggerNode) => triggerNode.parentNode as HTMLElement}
                        loading={isReviewStatusListInProgress || isUpdating || isLoading}
                        onDropdownVisibleChange={handleCloseSelect}
                        onSelect={onStatusSelect}
                        open={isOpen}
                        optionFilterProp="children"
                        placeholder={<Locale stringKey="REVIEWS__FILTER__SELECT_A_STATUS" />}
                        showSearch
                        value={[
                            isStatusMissingFromList
                                ? getLocalizedString('REVIEWS__STATUS__REPLACING_IN_PROGRESS')
                                : status?.title || '',
                        ]}
                    >
                        {mappedStatuses
                            ? mappedStatuses.get(innerStatus)?.map((item) => (
                                  <Option key={item} value={item}>
                                      {
                                          reviewStatusList?.statuses.find((findItem) => findItem.statusCode === item)
                                              ?.title
                                      }
                                  </Option>
                              ))
                            : reviewStatusList?.statuses.map(
                                  (reviewStatus: ReviewStatusType): JSX.Element => (
                                      <Option
                                          key={reviewStatus.statusCode}
                                          label={reviewStatus.title}
                                          value={reviewStatus.statusCode}
                                      >
                                          {renderStatusOption(reviewStatus)}
                                      </Option>
                                  )
                              )}
                    </Select>
                </Tooltip>
            </div>
            {shouldShowTour && <Tour onClose={handleCloseTour} open={isTourOpen} steps={tourSteps} />}
        </>
    );
});
